import { Dispatch, FC, SetStateAction, useState } from 'react';

import {Alert, Form as FormAnt, message, Typography } from 'antd';
import { UploadChangeParam } from 'antd/es/upload';
import { UploadFile, UploadProps } from 'antd/lib';

import { ColSize, ConfirmationModal, DraggerUploadFile, Form, RowSize, SelectForm } from '../';
import { Modal } from '@hdi-seguros/web-components.modal';
import { UploadedFile } from '../../interfaces/uploaded-file.interface';
import { DocumentType} from '../../interfaces/uploaded-file.interface';
import { ActionButton } from '@hdi-seguros/web-components.action-button';
import { SelectOption } from '../../interfaces';
import { documentTypes, documentTypesRV } from '../../utils/fileTypes';
import { fileToBase64DataUrl } from '../../utils/fillToBase64';

const DEFAULT_OTROS_DOCUMENT_TYPE: DocumentType = {
  code: 100,
  value: 'DENUNCIA_OTROS',
  label: 'Otros',
};

interface UploadFileModalProps {
  canShowUploadModalUploadFile: boolean;
  canSelectDocumentType: boolean;
  isRV: boolean;
  formatFile: string;
  setCanShowUploadModalUploadFile: Dispatch<SetStateAction<boolean>>;
  setFileSelect: Dispatch<UploadedFile[]>;
}

export const UploadFileModal: FC<UploadFileModalProps> = ({
  canShowUploadModalUploadFile,
  canSelectDocumentType,
  isRV,
  formatFile,
  setCanShowUploadModalUploadFile,
  setFileSelect,
}) => {
  const [fileSelectUpload, setFileSelectUpload] = useState<any[]>([]);

  const [form] = FormAnt.useForm<any>();
  const [documentTypeSelect, setDocumentTypeSelect] = useState<DocumentType | null>(null);
  const [showProceedWithoutTypeModal, setShowProceedWithoutTypeModal] = useState(false);
  const [showDefaultOtrosModal, setShowDefaultOtrosModal] = useState(false);

  const buildDocumentTypesCombo = (docs: any[]): SelectOption[] => {
  return docs
    .filter(doc => doc.code !== 12 && doc.code !== 13)
    .map(doc => ({
      value: doc.value,
      label: doc.label,
    }));
};

const documentTypesCombo = buildDocumentTypesCombo(
  isRV ? documentTypesRV : documentTypes
);

  const openFile = (file: any) => {
    let messageError: string =
      'No hay archivo seleccionado o el archivo está vacío.';

    if (file.base) {
      const newWindow = window.open();

      if (newWindow) {
        const document: string = `<iframe src="${file.base}" style="width:100%; height:100%;"></iframe>`;
        newWindow.document.write(document);
        return;
      }

      messageError =
        'La ventana emergente no se pudo abrir. Asegúrate de que las ventanas emergentes no estén bloqueadas en tu navegador.';
    }

    message.error(messageError);
  };

  const allowedTypes = ['image/png', 'image/jpeg', 'image/pjpeg', 'application/pdf'];
  const allowedExtensions = ['.png', '.jpg', '.jpeg', '.jfif', '.pdf'];
  const maxFileSize = 10 * 1024 * 1024;
  const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
  const isAllowedType = (type: string) => ALLOWED_TYPES.includes(type);

  const handleFileSelection = async (
    info: UploadChangeParam<UploadFile<any>>
  ) => {

    if (info.file.status === 'removed') {
      if(canSelectDocumentType){
        const updatedFiles = fileSelectUpload?.filter(
          (file: { uid: string }) => file.uid !== info.file.uid
        );
        setFileSelectUpload(updatedFiles);
      } else {
        setFileSelectUpload([]);
      }

      return;
    }
    if(canSelectDocumentType){

      const files = await Promise.all(
        info.fileList.map(async (file) => {
          if (file.originFileObj instanceof Blob) {

          const extension = `.${file.name.split('.').pop()?.toLowerCase()}`;
          const isValid = allowedTypes.includes(file.originFileObj.type) && allowedExtensions.includes(extension);


           if (!isValid) {
              message.error(`El tipo de archivo ${file.name} no está permitido. Solo se aceptan PDF, JPG y PNG.`);              
              return null;
            }

            if (file.originFileObj.size > maxFileSize) {
              message.error(`El archivo ${file.name} supera el tamaño máximo de 10MB.`);
              return null;
            }

            const result = await fileToBase64DataUrl(file.originFileObj) as string;

            const base64 = result.split(',')[1];
            return {
              base: base64,
              mediaType: file.type,
              name: file.name,
              size: (file.originFileObj.size / 1024 / 1024).toFixed(2),
              uid: file.uid,
              documentType: documentTypeSelect,
            };
          }

          return file;
        })
      );
      setFileSelectUpload(files.filter((file) => file !== null));

    } else {
      const file = info.file;

      if (file.originFileObj instanceof Blob) {
        if (!isAllowedType(file.originFileObj.type)) {
          message.error(`El tipo de archivo ${file.name} no está permitido. Solo se aceptan PDF, JPG y PNG.`);
          return null;
        }
        if (file.originFileObj.size > maxFileSize) {
          message.error(`El archivo ${file.name} supera el tamaño máximo de 10MB.`);
          return null;
        }
        const base64 = await fileToBase64DataUrl(file.originFileObj);
        setFileSelectUpload([
          {
            uid: file.uid,
            name: file.name,
            mediaType: file.type,
            base: base64,
            type: info.file.type?.split('/')[1],
          },
        ]);
      }
    }
  };

  const handleFileDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    const files = Array.from(event.dataTransfer.files);

    const allValidFiles = Array.from(event.dataTransfer.files).filter(
      (file) => allowedTypes.includes(file.type) && file.size <= maxFileSize
    );

    if (!allValidFiles.length) return;

    try {
      const fileList = await Promise.all(
        allValidFiles.map(async (file) => {
          const result = await fileToBase64DataUrl(file) as string;
          const base64 = result.split(',')[1];
          return {
            name: file.name,
            mediaType: file.type,
            base: base64,
          };
        })
      );

      setFileSelectUpload((prevFiles: any) => [...prevFiles, ...fileList.filter(f => f !== null)]);
    } catch (error) {
      message.error('Error al cargar los archivos');
    }
  };

  const currentDocumentTypes = isRV ? documentTypesRV : documentTypes;

  const getDefaultOtrosDocumentType = (): DocumentType =>
    currentDocumentTypes.find(
      (doc) => doc.value === 'DENUNCIA_OTROS' || doc.label.toLowerCase() === 'otros'
    ) ?? DEFAULT_OTROS_DOCUMENT_TYPE;

  const handleDocumentTypeMethod = (documentType : String) => {
    const selectedDocument = currentDocumentTypes.find(doc => doc.value === documentType) || null;
    setDocumentTypeSelect(selectedDocument);
  };

  const draggerProps: UploadProps = {
    name: 'file',
    multiple: canSelectDocumentType ? true : false,
    customRequest: () => false,
    onChange: (info) => {
      handleFileSelection(info);
    },
    onDrop: (e) => {
      handleFileDrop(e);
    },
  };

  const resetModalState = () => {
    setFileSelectUpload([]);
    setDocumentTypeSelect(null);
    setShowProceedWithoutTypeModal(false);
    setShowDefaultOtrosModal(false);
    form.resetFields();
  };

  const finalizeConfirm = (files: any[]) => {
    setCanShowUploadModalUploadFile(false);
    setFileSelect([...files as UploadedFile[]]);
    resetModalState();
  };

  const handleConfirm = () => {
    if (canSelectDocumentType && !documentTypeSelect) {
      setShowProceedWithoutTypeModal(true);
      return;
    }

    finalizeConfirm(fileSelectUpload);
  };

  const handleProceedWithoutTypeConfirm = () => {
    setShowProceedWithoutTypeModal(false);
    setShowDefaultOtrosModal(true);
  };

  const handleDefaultOtrosConfirm = () => {
    const otrosDocumentType = getDefaultOtrosDocumentType();
    const filesWithDefaultType = fileSelectUpload.map((file) => ({
      ...file,
      documentType: otrosDocumentType,
    }));

    finalizeConfirm(filesWithDefaultType);
  };

  const handleCancel = () => {
    setCanShowUploadModalUploadFile(false);
    resetModalState();
  };

  return (
    <>
    <Modal
      centered
      footer={
        <>
          <ActionButton
            label='Cancelar'
            type="secondary"
            onClick={handleCancel}
          />

          <ActionButton 
            label='Confirmar'
            type="primary"
            onClick={handleConfirm}
            disabled={!fileSelectUpload.length}
          />
        </>
      }
      onCancel={handleCancel}
      open={canShowUploadModalUploadFile}
      title= {canSelectDocumentType ? 'Carga de Documentos Relacionados' : 'Cargar archivos'}
      width={650}
    >
       <Form formProps={{
                form,
                name: "documentation-form",
                className: "documentation-form-container"
            }}>
      <RowSize className='mb-1' rowProps={{ gutter: 0 }}>
        
        {canSelectDocumentType &&
          <ColSize size={100}>
            <RowSize rowProps={{ gutter: 0 }}>
              <Alert
                showIcon
                type="info"
                message="Recuerde:"
                description={
                  <>
                    <Typography.Text>
                      - Seleccione el tipo y el archivo de cada documento.
                    </Typography.Text>
                    <br />
                    <Typography.Text>
                      - Al finalizar la selección de <b>todos</b> los documentos, haga clic en <b>“Confirmar”</b>.
                    </Typography.Text>
                    <br />
                    <Typography.Text>
                      - Cada archivo quedará asociado al tipo de documento previamente elegido.
                    </Typography.Text>
                  </>
                }
              />
            </RowSize>
          </ColSize>
        }

          {canSelectDocumentType && <ColSize size={100}><SelectForm
            formProps={{
              label: 'Tipo de documento:',
              name: 'documentType',
            }}
            selectProps={{
              options: documentTypesCombo,
              loading: documentTypesCombo.length === 0,
              disabled: documentTypesCombo.length === 0,
              showSearch: true,
              onChange: (documentType) => {
                handleDocumentTypeMethod(documentType);
            },
            }}
          />
        </ColSize>}
        <ColSize size={100}>
          <RowSize rowProps={{ gutter: 0, justify: 'center' }}>
            <DraggerUploadFile
              draggerProps={draggerProps}
              fileList={fileSelectUpload}
              formatFile={formatFile}
              onPreview={(file) => openFile(file)}
              multiple={canSelectDocumentType ? true : false }
            />
          </RowSize>
        </ColSize>
      </RowSize>
      </Form>
    </Modal>

    <ConfirmationModal
      open={showProceedWithoutTypeModal}
      title="Tipo de documento no seleccionado"
      description={
        <>
          <div>No seleccionó una clasificación para el documento adjuntado.</div>
          <br />
          <div>¿Desea proceder sin especificarlo?</div>
        </>
      }  
      confirmButtonLabel="Sí"
      onConfirm={handleProceedWithoutTypeConfirm}
      onCancel={() => setShowProceedWithoutTypeModal(false)}
    />

    <ConfirmationModal
      open={showDefaultOtrosModal}
      title="Tipo de documento no seleccionado"
      description={
        <>
          <div>Se establecerá <b>«Otros»</b> como clasificación por defecto.</div>
          <br />
          <div>¿Desea continuar?</div>
        </>
      }      
      confirmButtonLabel="Aceptar"
      onConfirm={handleDefaultOtrosConfirm}
      onCancel={() => setShowDefaultOtrosModal(false)}
    />
  </>
  );
};
